
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import SavePromptButton from "./SavePromptButton";
import { Link } from "react-router-dom";

interface PromptCardHeaderProps {
  id: string;
  title: string;
  description: string;
  category: string;
  category_id?: string;
  copyCount: number;
  isCopied: boolean;
  onCopy: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
  isLiked?: boolean;
  likeCount?: number;
  reviewCount?: number;
  onToggleLike?: (e: React.MouseEvent) => void;
}

const PromptCardHeader = ({
  id,
  title,
  description,
  category,
  copyCount,
  isCopied,
  onCopy,
  onClick,
  category_id,
}: PromptCardHeaderProps) => {
  const truncateDescription = (desc: string, maxLength = 80) => {
    if (desc.length <= maxLength) return desc;
    return `${desc.slice(0, maxLength)}...`;
  };

  // Format category if it's a JSON string or object
  const formatCategory = (categoryValue: string | any): string => {
    if (!categoryValue) return 'Uncategorized';

    if (typeof categoryValue === 'string') {
      // Try parsing as JSON if it looks like JSON
      if (categoryValue.startsWith('{')) {
        try {
          const categoryObj = JSON.parse(categoryValue);
          return categoryObj.name || 'Uncategorized';
        } catch (error) {
          // If parsing fails, return as is
          return categoryValue;
        }
      }
      return categoryValue;
    }
    
    if (typeof categoryValue === 'object' && categoryValue !== null) {
      if ('name' in categoryValue) {
        return categoryValue.name;
      }
    }
    
    return 'Uncategorized';
  };

  const categoryName = formatCategory(category);

  return (
    <div className="mb-3">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground mb-2">
            {truncateDescription(description)}
          </p>
          {category && (
            <Link 
              to={`/explore?category=${encodeURIComponent(category_id || '')}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Badge
                variant="secondary"
                className="hover:bg-secondary/80 text-xs"
              >
                {categoryName}
              </Badge>
            </Link>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <SavePromptButton 
            promptId={id} 
            promptTitle={title} 
            onClick={(e) => e.stopPropagation()} 
          />
          
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:text-primary hover:bg-primary/10"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(e);
            }}
            title="Copy to clipboard"
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            <span className="sr-only">Copy</span>
          </Button>
          <span className="text-xs text-muted-foreground">{copyCount}</span>
        </div>
      </div>
    </div>
  );
};

export default PromptCardHeader;
